import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  Card,
  Row,
  Col,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  SettingOutlined,
  DollarOutlined,
  PercentageOutlined,
  FunctionOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type {
  HRPayrollItem,
  PayrollItemFilters,
  PayrollItemFormData,
} from "../../types/hr";
import PayrollItemForm from "./PayrollItemForm";
import { useHRAPI } from "../../hooks/useHRAPI";

const { Title, Text } = Typography;
const { Option } = Select;

const PayrollItemsList: React.FC = () => {
  const [payrollItems, setPayrollItems] = useState<HRPayrollItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<HRPayrollItem | null>(null);
  const [filters, setFilters] = useState<PayrollItemFilters>({});
  const [searchText, setSearchText] = useState("");

  const {
    getPayrollItems,
    createPayrollItem,
    updatePayrollItem,
    deletePayrollItem,
    createDefaultPayrollItems,
  } = useHRAPI();

  useEffect(() => {
    fetchPayrollItems();
  }, []);

  const fetchPayrollItems = async () => {
    setLoading(true);
    try {
      const items = await getPayrollItems();
      setPayrollItems(items || []);
    } catch (error) {
      console.error("Erreur lors du chargement des éléments de paie:", error);
      message.error("Erreur lors du chargement des éléments de paie");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormVisible(true);
  };

  const handleEdit = (item: HRPayrollItem) => {
    setEditingItem(item);
    setFormVisible(true);
  };

  const handleDelete = async (item: HRPayrollItem) => {
    try {
      await deletePayrollItem(item.id);
      message.success("Élément de paie supprimé avec succès");
      fetchPayrollItems();
    } catch (error: any) {
      message.error(error.message || "Erreur lors de la suppression");
    }
  };

  const handleFormSubmit = async (values: PayrollItemFormData) => {
    try {
      if (editingItem) {
        await updatePayrollItem(editingItem.id, values);
        message.success("Élément de paie modifié avec succès");
      } else {
        await createPayrollItem(values);
        message.success("Élément de paie créé avec succès");
      }
      setFormVisible(false);
      fetchPayrollItems();
    } catch (error: any) {
      message.error(error.message || "Erreur lors de la sauvegarde");
    }
  };

  const handleCreateDefaults = async () => {
    try {
      await createDefaultPayrollItems();
      message.success("Éléments de paie par défaut créés avec succès");
      fetchPayrollItems();
    } catch (error: any) {
      message.error(
        error.message || "Erreur lors de la création des éléments par défaut"
      );
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "gain":
        return "green";
      case "deduction":
        return "red";
      case "info":
        return "blue";
      default:
        return "default";
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case "gain":
        return "Gain";
      case "deduction":
        return "Déduction";
      case "info":
        return "Information";
      default:
        return type;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "salary":
        return "purple";
      case "allowance":
        return "cyan";
      case "bonus":
        return "gold";
      case "deduction":
        return "red";
      case "tax":
        return "volcano";
      case "social_contribution":
        return "orange";
      case "loan":
        return "magenta";
      case "advance":
        return "geekblue";
      default:
        return "default";
    }
  };

  const getCategoryText = (category: string) => {
    switch (category) {
      case "salary":
        return "Salaire";
      case "allowance":
        return "Indemnité";
      case "bonus":
        return "Prime";
      case "deduction":
        return "Déduction";
      case "tax":
        return "Impôt";
      case "social_contribution":
        return "Cotisation sociale";
      case "loan":
        return "Prêt";
      case "advance":
        return "Avance";
      case "info":
        return "Information";
      default:
        return category;
    }
  };

  const getCalculationIcon = (type: string) => {
    switch (type) {
      case "fixed":
        return <DollarOutlined />;
      case "percentage":
        return <PercentageOutlined />;
      case "formula":
        return <FunctionOutlined />;
      default:
        return <DollarOutlined />;
    }
  };

  const getCalculationText = (type: string) => {
    switch (type) {
      case "fixed":
        return "Montant fixe";
      case "percentage":
        return "Pourcentage";
      case "formula":
        return "Formule";
      default:
        return type;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const filteredItems = payrollItems.filter((item) => {
    const matchesSearch =
      !searchText ||
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.code.toLowerCase().includes(searchText.toLowerCase()) ||
      item.custom_description?.toLowerCase().includes(searchText.toLowerCase());

    const matchesType = !filters.type || item.type === filters.type;
    const matchesCategory =
      !filters.category || item.category === filters.category;
    const matchesActive =
      filters.is_active === undefined || item.is_active === filters.is_active;

    return matchesSearch && matchesType && matchesCategory && matchesActive;
  });

  const columns: ColumnsType<HRPayrollItem> = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 100,
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.custom_description && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.custom_description}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type) => (
        <Tag color={getTypeColor(type)}>{getTypeText(type)}</Tag>
      ),
    },
    {
      title: "Catégorie",
      dataIndex: "category",
      key: "category",
      width: 130,
      render: (category) => (
        <Tag color={getCategoryColor(category)}>
          {getCategoryText(category)}
        </Tag>
      ),
    },
    {
      title: "Calcul",
      key: "calculation",
      width: 150,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {getCalculationIcon(record.calculation_type)}
          <span>{getCalculationText(record.calculation_type)}</span>
          {record.calculation_type === "fixed" && record.base_amount > 0 && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              ({formatCurrency(record.base_amount)})
            </Text>
          )}
          {record.calculation_type === "percentage" &&
            record.percentage > 0 && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                ({record.percentage}%)
              </Text>
            )}
        </div>
      ),
    },
    {
      title: "Taxes",
      key: "taxes",
      width: 120,
      render: (_, record) => (
        <div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {record.subject_to_tax && <Badge status="success" text="IRPP" />}
            {record.subject_to_cnss && (
              <Badge status="processing" text="CNSS" />
            )}
            {record.subject_to_tpa && <Badge status="warning" text="TPA" />}
          </div>
          {!record.subject_to_tax &&
            !record.subject_to_cnss &&
            !record.subject_to_tpa && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Exonéré
              </Text>
            )}
        </div>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Modifier">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer cet élément ?"
              onConfirm={() => handleDelete(record)}
              okText="Oui"
              cancelText="Non"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const stats = {
    total: payrollItems.length,
    gains: payrollItems.filter((item) => item.type === "gain").length,
    deductions: payrollItems.filter((item) => item.type === "deduction").length,
    active: payrollItems.filter((item) => item.is_active).length,
  };

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Éléments de paie
            </Title>
            <Text type="secondary">
              Gérez les éléments de rémunération et de déduction
            </Text>
          </div>
          <Space>
            {payrollItems.length === 0 && (
              <Button
                type="default"
                icon={<SettingOutlined />}
                onClick={handleCreateDefaults}
              >
                Créer éléments par défaut
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Nouvel élément
            </Button>
          </Space>
        </div>
      </div>

      {/* Statistiques */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: "center" }}>
              <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
                {stats.total}
              </Title>
              <Text type="secondary">Total éléments</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: "center" }}>
              <Title level={2} style={{ margin: 0, color: "#52c41a" }}>
                {stats.gains}
              </Title>
              <Text type="secondary">Gains</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: "center" }}>
              <Title level={2} style={{ margin: 0, color: "#ff4d4f" }}>
                {stats.deductions}
              </Title>
              <Text type="secondary">Déductions</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: "center" }}>
              <Title level={2} style={{ margin: 0, color: "#faad14" }}>
                {stats.active}
              </Title>
              <Text type="secondary">Actifs</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={16}>
          <Col span={8}>
            <Input
              placeholder="Rechercher par nom, code ou description..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Type"
              value={filters.type}
              onChange={(value) => setFilters({ ...filters, type: value })}
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="gain">Gain</Option>
              <Option value="deduction">Déduction</Option>
              <Option value="info">Information</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Catégorie"
              value={filters.category}
              onChange={(value) => setFilters({ ...filters, category: value })}
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="salary">Salaire</Option>
              <Option value="allowance">Indemnité</Option>
              <Option value="bonus">Prime</Option>
              <Option value="deduction">Déduction</Option>
              <Option value="tax">Impôt</Option>
              <Option value="social_contribution">Cotisation sociale</Option>
              <Option value="loan">Prêt</Option>
              <Option value="advance">Avance</Option>
              <Option value="info">Information</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Statut"
              value={filters.is_active}
              onChange={(value) => setFilters({ ...filters, is_active: value })}
              allowClear
              style={{ width: "100%" }}
            >
              <Option value={true}>Actif</Option>
              <Option value={false}>Inactif</Option>
            </Select>
          </Col>
          <Col span={2}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setFilters({});
                setSearchText("");
              }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredItems}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredItems.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} éléments`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Formulaire */}
      <PayrollItemForm
        visible={formVisible}
        onCancel={() => setFormVisible(false)}
        onSubmit={handleFormSubmit}
        initialValues={editingItem}
        isEditing={!!editingItem}
      />
    </div>
  );
};

export default PayrollItemsList;
